<template>
  <div class="cf-flex cf-flex--column cf-flex--gap-16">
    <sign-up-header />

    <cf-validated-form
      v-if="isSignUpFormDisplayed"
      :class="[{ 'animation-slide-left': merchant.thirdPartySignIn.enabled }]"
      name="createAccount"
      @valid-submit="formSubmitHandler"
    >
      <div class="cf-flex cf-flex--column cf-flex--gap-16">
        <cf-full-name-input v-model="fullName" />

        <cf-text-input
          v-if="userValidations.company && !userValidations.company.signupDisabled"
          v-model="company"
          type="text"
          label="Company Name"
          name="company"
          icon-class="fa-building"
          autocomplete="organization"
          :show-as-optional="true"
          :validations="{
            required: userValidations.company.isRequired
          }"
        />

        <cf-text-input
          v-if="userValidations.email && !userValidations.email.signupDisabled"
          v-model="email"
          type="email"
          label="Email"
          name="emailCreateAccount"
          autocomplete="email"
          :show-as-optional="true"
          :validations="{
            required: userValidations.email.isRequired
          }"
        />

        <cf-phone-number-input
          v-if="userValidations.primarySmsNumber && !userValidations.primarySmsNumber.signupDisabled"
          v-model="phoneNumber"
          label="Phone Number"
          name="phoneCreateAccount"
          :show-as-optional="true"
          :validations="{
            required: userValidations.primarySmsNumber.isRequired
          }"
        />

        <cf-text-input
          v-if="userValidations.favoriteStoreId && !userValidations.favoriteStoreId.signupDisabled"
          type="text-as-button"
          label="Favorite Store"
          name="favoriteStore"
          icon-class="fa-map-marker-alt"
          autocomplete="off"
          :model-value="favoriteStore && favoriteStore.description"
          :show-as-optional="!userValidations.favoriteStoreId.isRequired"
          :validations="{
            required: userValidations.favoriteStoreId.isRequired
          }"
          @click="openFavoriteStoreModal"
        />

        <template v-if="showZipField">
          <cf-country-select
            v-model="country"
            :countries="merchant.regions.map(r => r.abbreviation)"
          />

          <cf-text-input
            v-model="zip"
            type="text"
            :label="postalCodeLabel"
            name="zip"
            :mask="{ type: 'postalCode', region: country }"
            icon-class="fa-map-marker-alt"
            inputmode="numeric"
            autocomplete="postal-code"
            :show-as-optional="true"
            :validations="postalCodeValidations"
          />
        </template>

        <cf-birthday-input
          v-if="userValidations.birthdayInfo && !userValidations.birthdayInfo.signupDisabled"
          v-model="dateOfBirth"
          :include-year="!!userValidations['birthdayInfo.year']"
          :required="userValidations.birthdayInfo.isRequired"
        />

        <cf-text-input
          v-if="userValidations.password"
          v-model="password"
          type="password"
          label="Password"
          name="passwordCreateAccount"
          :show-as-optional="true"
          :validations="{
            required: userValidations.password.isRequired,
            strong_password: true
          }"
        />

        <cf-text-input
          v-if="userValidations.inviteCode && !userValidations.inviteCode.signupDisabled"
          v-model="inviteCode"
          type="text"
          label="Invite Code"
          name="inviteCode"
          :show-as-optional="true"
          :validations="{
            required: userValidations.inviteCode.isRequired,
            alpha_num: true,
          }"
        />

        <div class="cf-flex cf-flex--column cf-flex--gap-16">
          <template v-if="userValidations.smsNotificationCheckbox && !userValidations.smsNotificationCheckbox.signupDisabled">
            <cf-switch-input
              v-model="smsOptIn"
              :show-info-icon="!showSmsDisclaimer"
              :description="smsTermsContent"
              label="Get Text Updates"
              name="smsOptIn"
            />

            <p
              v-if="showSmsDisclaimer"
              class="cf-subtitle cf-subtitle-3"
              v-html="smsTermsContent"
            />

            <hr class="no-spacing no-spacing-top">
          </template>

          <cf-checkbox-input
            v-model="termsAccepted"
            label="Terms of Use"
            name="termsAccepted"
            :validations="{ required: true }"
            class="cf-text-align-left"
          >
            <cf-consent-terms :content="consentContent">
              I agree to the
              <button
                class="cf-link cf-link--brand-secondary cf-link--heavy"
                type="button"
                @click="openPrivacyPolicyHandler"
              >
                Privacy Policy
              </button>{{ termsLinkSeparators.afterPrivacyPolicy }}
              <button
                class="cf-link cf-link--brand-secondary cf-link--heavy"
                type="button"
                @click="openTermsAndConditionsHandler"
              >
                Terms & Conditions
              </button>{{ termsLinkSeparators.afterTermsAndConditions }}
              <button
                v-if="generalNotice.isAvailable"
                class="cf-link cf-link--brand-secondary cf-link--heavy"
                type="button"
                @click="openGeneralNoticeHandler"
              >
                General Notice
              </button>
            </cf-consent-terms>
          </cf-checkbox-input>

          <p
            v-if="enrollmentOptions.marketingOptIn"
            class="cf-subtitle cf-subtitle--2 cf-text-align-left cf-content"
            v-html="dynamicContent.CreateAccount.metadata.termsAndConditions"
          />

          <p
            v-if="isLoyaltySignOnEnabled"
            class="cf-subtitle cf-subtitle--2 cf-text-align-left cf-content"
            v-html="ssoLoyaltyContent.ssoTerms"
          />

          <button
            :class="['cf-button cf-button--primary cf-button--lg cf-button--fill', { 'cf-button--loading': isLoading }]"
            type="submit"
          >
            {{ dynamicContent.CreateAccount.metadata.buttonCta }}
          </button>
        </div>
      </div>
    </cf-validated-form>

    <div
      v-else
      class="cf-flex cf-flex--column cf-flex--gap-16"
    >
      <button
        class="cf-button cf-button--primary cf-button--lg cf-button--fill"
        type="button"
        @click="signUpWithEmailClickHandler"
      >
        Sign Up With Email
      </button>

      <template v-if="isThirdPartySignInEnabled">
        <cf-title-separator />

        <cf-third-party-authentication
          auth-type="sign-up"
        />
      </template>
    </div>

    <div class="cf-modal__footer">
      <p class="cf-subtitle cf-subtitle--2 cf-subtitle--black">
        Already have an account?

        <button
          class="cf-link cf-link--brand-secondary cf-link--heavy"
          type="button"
          @click="openSignInModalHandler"
        >
          Sign In
        </button>
      </p>
    </div>
  </div>
</template>


<script>
  import { mapGetters } from 'vuex';

  import errorHandlers from '@/services/error-handlers';
  import events from '@/services/events';

  import parseTwoFactorError from '@/helpers/parse-two-factor-error';
  import { getCountryMeta } from '@/helpers/address-format';
  import { detectDefaultCountry } from '@/helpers/detect-default-country';

  import SignUpHeader from '../sign-up-header/index.vue';
  import CfTextInput from '@/view/components/shared/cf-text-input/index.vue';
  import CfPhoneNumberInput from '@/view/components/shared/cf-phone-number-input/index.vue';
  import CfCountrySelect from '@/view/components/shared/cf-country-select/index.vue';
  import CfFullNameInput from '@/view/components/shared/cf-full-name-input/index.vue';
  import CfBirthdayInput from '@/view/components/shared/cf-birthday-input/index.vue';
  import CfSwitchInput from '@/view/components/shared/cf-switch-input/index.vue';
  import CfCheckboxInput from '@/view/components/shared/cf-checkbox-input/index.vue';
  import CfConsentTerms from '@/view/components/shared/cf-consent-terms/index.vue';
  import CfTitleSeparator from '@/view/components/shared/cf-title-separator/index.vue';
  import CfValidatedForm from '@/view/components/shared/cf-validated-form/index.vue';

  import CfThirdPartyAuthentication from '@/view/components/globals/cf-third-party-authentication/index.vue';


  export default {
    name: 'SignUp',

    components: {
      SignUpHeader,
      CfCountrySelect,
      CfTextInput,
      CfPhoneNumberInput,
      CfFullNameInput,
      CfBirthdayInput,
      CfValidatedForm,
      CfSwitchInput,
      CfCheckboxInput,
      CfConsentTerms,
      CfTitleSeparator,
      CfThirdPartyAuthentication
    },

    data() {
      return {
        company: null,
        country: 'US',
        dateOfBirth: null,
        email: null,
        favoriteStore: null,
        fullName: null,
        isLoading: false,
        overrideWelcomeToLoyalty: false,
        password: null,
        phoneNumber: null,
        isSignUpFormDisplayed: false,
        smsOptIn: false,
        termsAccepted: true,
        zip: null,
        inviteCode: null
      };
    },

    computed: {
      ...mapGetters('content', ['content', 'dynamicContent', 'generalNotice']),
      ...mapGetters('merchant', ['enrollmentOptions', 'loyaltyOptions', 'merchant']),
      ...mapGetters('offers', ['loyalty', 'offers']),
      ...mapGetters('session', ['session']),

      consentContent() {
        return this.dynamicContent.CreateAccount.metadata.agreeToCardFreePrivacyAndTerms;
      },

      termsLinkSeparators() {
        return this.generalNotice.isAvailable
          ? { afterPrivacyPolicy: ',', afterTermsAndConditions: ', and' }
          : { afterPrivacyPolicy: ' and', afterTermsAndConditions: '' };
      },

      showSmsDisclaimer() {
        return this.merchant.options.showSmsDisclaimer;
      },

      smsTermsContent() {
        return this.dynamicContent.CreateAccount.metadata.smsTerms;
      },

      loyaltyContent() {
        return this.content.PayAtTableCheckoutLoyaltyOffer;
      },

      ssoLoyaltyContent() {
        const { metadata } = this.content.CreateAccountLoyalty;

        const { loyaltyPrivacyPolicyAndTermsOfService: ssoTerms } = metadata;

        return { ssoTerms };
      },

      isThirdPartySignInEnabled() {
        return this.merchant.thirdPartySignIn.enabled;
      },

      isLoyaltySignOnEnabled() {
        return this.loyaltyOptions.supportsLoyaltySso;
      },

      userValidations() {
        const userValidations = {};

        Object.entries(this.merchant.userSignUpFields).forEach(([signUpField, signUpFieldValues]) => {
          const hasPlatFormTypeProperty = Object.prototype.hasOwnProperty.call(signUpFieldValues, 'platformTypes');

          const isDisplayed = (hasPlatFormTypeProperty && signUpFieldValues.platformTypes.find(type => type === 'Web')) || !hasPlatFormTypeProperty;

          if (isDisplayed) {
            Object.assign(userValidations, { [signUpField]: { ...signUpFieldValues } });
          }
        });

        return userValidations;
      },

      showZipField() {
        return !!this.userValidations.zip && !this.userValidations.zip.signupDisabled;
      },

      postalCodeLabel() {
        return getCountryMeta(this.country).labels.zip;
      },

      postalCodeValidations() {
        const meta = getCountryMeta(this.country);
        const validations = { required: this.userValidations.zip.isRequired };
        if (meta.postalCodeRegex) {
          validations.regex = meta.postalCodeRegex;
        }
        return validations;
      }
    },

    mounted() {
      this.onComponentMounted();
    },

    methods: {
      onComponentMounted() {
        const { smsNotificationCheckbox } = this.merchant.userSignUpFields;

        this.country = detectDefaultCountry(this.merchant.regions.map(r => r.abbreviation));

        const inviteCode = this.$route.query.referredBy;

        if (inviteCode) this.inviteCode = inviteCode;

        if (!this.merchant.thirdPartySignIn.enabled) {
          this.isSignUpFormDisplayed = true;
        }

        if (smsNotificationCheckbox && smsNotificationCheckbox.isRequired) {
          this.smsOptIn = true;
        }

        if (this.session.prefillAccountInfo) {
          this.email = this.session.prefillAccountInfo.email;
          this.fullName = this.session.prefillAccountInfo.fullName;
          this.primarySmsNumber = this.session.prefillAccountInfo.primarySmsNumber;
        }

        if (this.offers.promoCode.claimCode) {
          this.overrideWelcomeToLoyalty = true;
        }

        if (this.offers.giftingPoints.pointGiftClaimCode) {
          this.overrideWelcomeToLoyalty = true;

          const mutationPayload = { claimGiftedPointsFlow: 'CREATED_ACCOUNT' };

          this.$store.commit('offers/SET_GIFTING_POINTS_DATA', mutationPayload);
        }
      },

      signUpWithEmailClickHandler() {
        this.isSignUpFormDisplayed = true;
      },

      async formSubmitHandler() {
        this.isLoading = true;

        const dispatchPayload = this.formatCreateAccountPayload();

        const { error } = await this.$store.dispatch('session/createAccount', dispatchPayload);

        if (error) {
          this.handleError(error);
        }
        else {
          this.handleSuccess();
        }

        this.isLoading = false;
      },

      handleError(error) {
        const { requiresAuth, ...rest } = parseTwoFactorError(error, this.email, this.phoneNumber);

        if (requiresAuth) {
          this.overrideWelcomeToLoyalty = true;

          const twoFactorModalPayload = { ...rest, onSuccess: this.handleSuccess };

          this.$openModal('twoFactorAuthModal', twoFactorModalPayload, { isRequired: true });
        }
        else {
          errorHandlers.generic(error);

          // this.$triggerGoogleTrackingEvent('CreateAccountFailure', 'Account');

          this.$triggerNewRelicEvent('CreateAccountFailure', 'Error', 'Create Account Failure');
        }
      },

      async handleSuccess() {
        const { signInRedirectRoute } = this.session;

        const notificationEventPayload = { type: 'success', message: 'Welcome! Please check your email.', clickToDismiss: true };

        events.emit('notification', notificationEventPayload);

        await this.checkForRetroClaims();

        this.$closeModal('createAccount');

        if (!this.overrideWelcomeToLoyalty && this.loyalty.isEnrolled) {
          const modalBody = {
            title: this.content.LoyaltyEnrollmentSuccess.title,
            description: this.content.LoyaltyEnrollmentSuccess.description,
            imageUrl: this.content.LoyaltyEnrollmentSuccess.metadata.imageUrl,
            ctaConfirmText: this.content.LoyaltyEnrollmentSuccess.metadata.buttonCta
          };

          this.$openModal('notice-modal', modalBody);
        }

        if (signInRedirectRoute) this.redirect(signInRedirectRoute);

        // this.$triggerGoogleTrackingEvent('CreateAccountSuccess', 'Account');

        this.$triggerNewRelicEvent('CreateAccountSuccess', 'Success', 'Create Account Success');
      },

      async checkForRetroClaims() {
        const { receiptToken } = this.$route.params;

        const retroClaimReceipt = this.offers.retroReceiptToken;

        if (receiptToken || retroClaimReceipt) {
          const dispatchPayload = { type: 'cfReceiptToken', value: receiptToken || retroClaimReceipt };

          await this.$store.dispatch('offers/claimPreviousOrder', dispatchPayload);
        }
      },

      formatCreateAccountPayload() {
        return {
          company: this.company,
          dateOfBirth: this.dateOfBirth,
          email: this.email,
          enrollInLoyalty: this.loyaltyOptions.loyaltyEnrollmentEnabled,
          favoriteStoreId: this.favoriteStore?.storeId,
          fullName: this.fullName,
          notificationPreferences: {
            email: this.enrollmentOptions.marketingOptIn,
            marketingOptIn: this.enrollmentOptions.marketingOptIn,
            sms: this.smsOptIn
          },
          password: this.password,
          primarySmsNumber: this.phoneNumber,
          zip: this.zip,
          inviteCode: this.inviteCode
        };
      },

      selectStore(store) {
        this.favoriteStore = store;
      },

      openFavoriteStoreModal() {
        const selectFavoriteStoreModalPayload = {
          selectedStoreId: this.favoriteStore?.storeId,
          onFavoriteStoreSelection: this.selectStore
        };

        this.$openModal('selectFavoriteStore', selectFavoriteStoreModalPayload);
      },

      openPrivacyPolicyHandler() {
        this.$openModal('privacyPolicy');
      },

      openTermsAndConditionsHandler() {
        this.$openModal('termsAndConditions');
      },

      openGeneralNoticeHandler() {
        this.$openModal('generalNotice');
      },

      openSignInModalHandler() {
        if (this.isLoyaltySignOnEnabled) {
          this.$openModal('single-sign-on-modal');
        }
        else {
          this.$openModal('signIn');
        }

        this.$closeModal('createAccount');
      },

      redirect(route) {
        const routerPayload = { name: route.name, params: route.params, query: route.query };

        this.$router.replace(routerPayload);
      }
    }
  };
</script>
